import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  BookOpen, 
  Play, 
  Clock, 
  Star, 
  Users, 
  Award, 
  Search, 
  ChevronRight,
  TrendingUp,
  Target,
  Zap,
  Heart,
  Globe,
  Crown,
  Check
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { useUserType } from '../hooks/useUserType';
import { usePremiumAccess } from '../hooks/usePremiumAccess';
import { useToast } from '../contexts/ToastContext';
import { fetchAllJourneyPaths } from '../lib/contentClient';
import { resolveField } from '../lib/contentText';
import { useContentLanguage } from '../contexts/ContentLanguageContext';
import type { JourneyPath } from '../types/journey';
import { ComingSoonBadge } from '../components/ComingSoonBadge';
import GuidesIntentWorkspace from '../features/intent-driven/integrations/guides/GuidesIntentWorkspace';

const GuidesPage: React.FC = () => {
  const navigate = useNavigate();
  const { } = useParams<{ slug?: string }>();
  const { user } = useAuth();
  const { userType, loading: userTypeLoading } = useUserType();
  const { hasPremiumAccess } = usePremiumAccess();
  
  const { showToast } = useToast();
  const { lang } = useContentLanguage();
  const pathTitle = (path: JourneyPath) => resolveField(lang, path.titleMy, path.titleEn, path.title).text;
  const pathSummary = (path: JourneyPath) => resolveField(lang, path.summaryMy, path.summaryEn, path.shortDescription).text;

  const [journeyPaths, setJourneyPaths] = useState<JourneyPath[]>([]);
  const [filteredPaths, setFilteredPaths] = useState<JourneyPath[]>([]);
  const [lessons, setLessons] = useState<any[]>([]);
  const [quickCourses, setQuickCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState<'journeys' | 'lessons' | 'quick-courses'>('journeys');
  const [navigatingItem, setNavigatingItem] = useState<string | null>(null);

  // Debug premium access
  console.log('=== GUIDES PAGE DEBUG ===');
  console.log('User premium access status:', hasPremiumAccess);
  console.log('User object:', user);
  console.log('User type:', userType);
  console.log('User ID:', user?.id);
  console.log('Is user authenticated:', !!user);
  console.log('Journey paths loaded:', journeyPaths.length);

  useSEO({
    title: 'Guides & Lessons - MyNewStart',
    description: 'Your learning hub with free guides and premium courses for deeper growth.',
  });

  // Check if a journey path is premium
  const isJourneyPremium = (path: JourneyPath): boolean => {
    const isPremium = path.isPremium || false;
    console.log(`=== JOURNEY PREMIUM CHECK ===`);
    console.log(`Journey "${path.title}" premium check:`, {
      journeyIsPremium: isPremium,
      hasPremiumAccess,
      userRole: user?.user_metadata?.role,
      pathData: { title: path.title, isPremium: path.isPremium }
    });
    console.log(`Button should show: ${isPremium && !hasPremiumAccess ? 'Unlock Premium' : 'Start Your Journey'}`);
    return isPremium;
  };

  // Launch-ready journeys are the only ones that can be started.
  // Free-but-empty Sanity paths used to pass this check and dead-end.
  const isJourneyComplete = (path: JourneyPath): boolean => {
    return path.launchReady === true;
  };

  useEffect(() => {
    loadData();
  }, [user, userType]);

  useEffect(() => {
    filterContent();
  }, [journeyPaths, searchTerm, selectedFilter, selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    
    try {
      // Fetch real journey paths from Sanity
      const sanityPaths = await fetchAllJourneyPaths(userType || undefined);
      console.log('Fetched journey paths from Sanity:', sanityPaths);
      
      // Set the real data from Sanity
      setJourneyPaths(sanityPaths);
      
      // These ids match QuickCoursePage. Do not point at slugs that page does not know.
      const mockLessons = [
        {
          _id: 'credit-basics',
          title: 'Credit Scores: Your Financial Passport in America',
          slug: 'credit-basics',
          description: 'Learn how credit scores work in the US and why they matter for your future',
          duration: '15 min',
          level: 'beginner',
          category: 'banking',
          isPremium: false,
          rating: 4.8,
          href: '/quick-course/credit-basics'
        },
        {
          _id: 'banking-system',
          title: 'Opening Your First US Bank Account',
          slug: 'banking-system',
          description: 'Checking, savings, debit cards, and what to bring when you open an account',
          duration: '12 min',
          level: 'beginner',
          category: 'banking',
          isPremium: false,
          rating: 4.9,
          href: '/quick-course/banking-system'
        }
      ];

      const mockQuickCourses = [
        {
          _id: 'banking-system',
          title: 'Banking System',
          slug: 'banking-system',
          description: 'Account types, debit versus credit, and online banking for newcomers',
          duration: '12 min',
          level: 'beginner',
          category: 'banking',
          isPremium: false,
          rating: 4.8
        },
        {
          _id: 'credit-basics',
          title: 'Credit Basics',
          slug: 'credit-basics',
          description: 'What a credit score is and the first habits that build one',
          duration: '15 min',
          level: 'beginner',
          category: 'banking',
          isPremium: false,
          rating: 4.9
        },
        {
          _id: 'housing-tips',
          title: 'Housing Tips',
          slug: 'housing-tips',
          description: 'Renting, applications, and the papers landlords ask for',
          duration: '10 min',
          level: 'beginner',
          category: 'housing',
          isPremium: false,
          rating: 4.7
        },
        {
          _id: 'healthcare-guide',
          title: 'Healthcare Guide',
          slug: 'healthcare-guide',
          description: 'Insurance, finding a doctor, and when to use urgent care',
          duration: '20 min',
          level: 'beginner',
          category: 'healthcare',
          isPremium: false,
          rating: 4.8
        }
      ];

      setLessons(mockLessons);
      setQuickCourses(mockQuickCourses);
      
    } catch (error) {
      console.error('Error loading journey paths:', error);
      showToast('error', 'Failed to load content. Please try again.');
      
      // Fallback to empty arrays if Sanity fails
      setJourneyPaths([]);
      setLessons([]);
      setQuickCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const filterContent = () => {
    let filtered = [...journeyPaths];

    if (searchTerm) {
      filtered = filtered.filter(path => 
        pathTitle(path).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (path.titleEn || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (path.titleMy || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedFilter === 'free') {
      filtered = filtered.filter(path => !path.isPremium);
    } else if (selectedFilter === 'premium') {
      filtered = filtered.filter(path => path.isPremium);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(path =>
        path.title.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }

    // Ready courses first, then free, then premium coming-soon paths.
    filtered.sort((a, b) => {
      if (Boolean(a.launchReady) !== Boolean(b.launchReady)) return a.launchReady ? -1 : 1;
      if (a.isPremium === b.isPremium) return 0;
      return a.isPremium ? 1 : -1;
    });

    setFilteredPaths(filtered);
  };

  const handleNavigation = (path: string, title: string, type: 'journey' | 'lesson' | 'course', itemId: string) => {
    if (navigatingItem) return;
    
    console.log(`Navigating to ${type}: ${path} - ${title}`);
    setNavigatingItem(itemId);
    showToast('info', `Starting ${title}`);
    
    setTimeout(() => {
      navigate(path);
      setNavigatingItem(null);
    }, 500);
  };

  const categories = [
    { id: 'all', name: 'All Topics', icon: Globe },
    { id: 'banking', name: 'Money & Banking', icon: Target },
    { id: 'taxes', name: 'Taxes & Finance', icon: TrendingUp },
    { id: 'housing', name: 'Home & Community', icon: Heart },
    { id: 'career', name: 'Work & Education', icon: Award }
  ];

  if (loading || userTypeLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/10 to-purple-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-4">
              <BookOpen className="w-6 h-6 text-primary-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Your Journey to Success in America
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              You're not alone in this journey. We're here to guide you step-by-step through everything you need to know to build your new life in America.
            </p>
            
            {/* Progress Indicator */}
            <div className="bg-white rounded-xl shadow-sm p-4 max-w-md mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  Your Progress
                </span>
                <span className="text-sm font-bold text-primary-600">12%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-gradient-to-r from-primary-500 to-purple-500 h-2 rounded-full transition-all duration-500" style={{width: '12%'}}></div>
              </div>
              <p className="text-xs text-gray-500">
                🎉 Great start! Keep building your foundation in America.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder='Search for help with banking, housing, taxes...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
          </div>
          <div className="flex flex-wrap gap-3">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Content</option>
                <option value="free">Free Resources</option>
                <option value="premium">Premium Guides</option>
              </select>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Topics</option>
                <option value="banking">Banking & Credit</option>
                <option value="taxes">Taxes & Finance</option>
                <option value="housing">Housing & Life</option>
                <option value="career">Career & Education</option>
              </select>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center px-4 py-2 rounded-full border transition-all duration-200 ${
              selectedCategory === category.id
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <category.icon className="w-4 h-4 mr-2" />
            {category.name}
          </button>
        ))}
      </div>

        {/* Main Navigation Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-8">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('journeys')}
              className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 ${
                activeTab === 'journeys'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Learning Paths</span>
              </div>
              <p className="text-xs mt-1 opacity-80">Step-by-step guides</p>
            </button>
            <button
              onClick={() => setActiveTab('lessons')}
              className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 ${
                activeTab === 'lessons'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Play className="w-5 h-5" />
                <span>Quick Lessons</span>
              </div>
              <p className="text-xs mt-1 opacity-80">Learn in minutes</p>
            </button>
            <button
              onClick={() => setActiveTab('quick-courses')}
              className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 ${
                activeTab === 'quick-courses'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Zap className="w-5 h-5" />
                <span>Quick Wins</span>
              </div>
              <p className="text-xs mt-1 opacity-80">5-15 min courses</p>
            </button>
        </div>
      </div>

      <div className="mb-8">
        <GuidesIntentWorkspace
          searchTerm={searchTerm}
          selectedFilter={selectedFilter}
          selectedCategory={selectedCategory}
          activeTab={activeTab}
          journeyPaths={journeyPaths}
          filteredPaths={filteredPaths}
          lessons={lessons}
          quickCourses={quickCourses}
          hasPremiumAccess={hasPremiumAccess}
          onStartJourney={(path) => {
            if (!isJourneyComplete(path)) {
              showToast('info', 'This course is coming soon.');
              return;
            }
            handleNavigation(`/courses/${path.slug}`, path.title, 'journey', path._id);
          }}
          onOpenSubscription={() => navigate('/subscription')}
          onSwitchTab={setActiveTab}
          onIntentSuccess={(message) => showToast('success', message)}
          onIntentFriction={(message) => showToast('info', message)}
        />
      </div>

      {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {activeTab === 'journeys' && filteredPaths.map((path) => (
          <div
            key={path._id}
            className="group bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl hover:border-primary-200 transition-all duration-500 transform hover:-translate-y-2"
          >
            <div className="relative h-56 overflow-hidden">
                <img
                  src={path.coverImage?.url || 'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800'}
                  alt={path.coverImage?.alt || path.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  path.isPremium 
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900' 
                    : 'bg-gradient-to-r from-green-400 to-green-500 text-green-900'
                }`}>
                  {path.isPremium ? '✨ Premium' : '🎉 Free'}
                </span>
                {!isJourneyComplete(path) && (
                  <ComingSoonBadge />
                )}
              </div>
              
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <span className="bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-xs font-medium">
                  ⏱️ {path.duration}
                </span>
                {isJourneyPremium(path) && !hasPremiumAccess && (
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                    <Crown className="h-3 w-3 mr-1" />
                    Premium
                  </span>
                )}
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                  📚 {path.level}
                </span>
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-sm font-medium text-gray-600">{path.rating}</span>
                </div>
              </div>

              <h3 className="font-myanmar text-xl font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors break-words">
                {path.primaryLanguage === 'en' ? (path.titleEn || pathTitle(path)) : pathTitle(path)}
              </h3>
              {path.primaryLanguage === 'en' && path.titleMy && (
                <p className="lesson-subtext font-myanmar mb-2" lang="my">{path.titleMy}</p>
              )}
              
                <p className={`font-myanmar text-gray-600 text-sm break-words ${path.primaryLanguage === 'en' && path.summaryMy ? '' : 'mb-4'}`}>
                  {path.primaryLanguage === 'en' ? (path.summaryEn || pathSummary(path)) : (pathSummary(path) || 'Learn essential skills for your journey in America')}
              </p>
              {path.primaryLanguage === 'en' && path.summaryMy && (
                <p className="lesson-subtext font-myanmar mb-4" lang="my">{path.summaryMy}</p>
              )}

              {path.journeyOutcomes && path.journeyOutcomes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">What You'll Learn</h4>
                  <div className="space-y-1">
                      {path.journeyOutcomes.slice(0, 2).map((outcome: any, idx: number) => (
                      <div key={idx} className="flex items-start text-xs text-gray-600">
                        <Check className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{typeof outcome === 'string' ? outcome : 'Learning objective'}</span>
                      </div>
                    ))}
                    {path.journeyOutcomes.length > 2 && (
                      <div className="text-xs text-primary-600 font-medium">
                        +{path.journeyOutcomes.length - 2} more outcomes
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{path.enrolled} learners</span>
                </div>
              </div>

                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Don't allow navigation if course is incomplete
                    if (!isJourneyComplete(path)) {
                      showToast('info', 'This course is coming soon! We\'re working hard to complete it.');
                      return;
                    }
                    
                    // Check if journey is premium and user doesn't have access
                    if (isJourneyPremium(path) && !hasPremiumAccess) {
                      navigate('/subscription');
                      return;
                    }
                    
                    handleNavigation(`/courses/${path.slug}`, path.title, 'journey', path._id);
                  }}
                  className={`w-full py-3 px-4 rounded-xl transition-all duration-300 flex items-center justify-center group shadow-lg hover:shadow-xl ${
                    !isJourneyComplete(path) || navigatingItem === path._id
                      ? 'opacity-75 cursor-not-allowed bg-gray-300 text-gray-600'
                      : isJourneyPremium(path) && !hasPremiumAccess
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white'
                        : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white'
                  }`}
                  disabled={!isJourneyComplete(path) || navigatingItem === path._id}
                >
                  {!isJourneyComplete(path) ? (
                    <>
                      <Clock className="w-5 h-5 mr-2" />
                      <span className="font-semibold">Coming Soon</span>
                    </>
                  ) : isJourneyPremium(path) && !hasPremiumAccess ? (
                    <>
                      <Crown className="w-5 h-5 mr-2" />
                      <span className="font-semibold">Unlock Premium</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      <span className="font-semibold">
                        {navigatingItem === path._id 
                          ? 'Starting...' 
                          : 'Start Learning'
                        }
                      </span>
                    </>
                  )}
              </button>
            </div>
          </div>
        ))}

          {activeTab === 'lessons' && lessons.map((lesson) => (
          <div
            key={lesson._id}
            className="group bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl hover:border-primary-200 transition-all duration-500 transform hover:-translate-y-2"
          >
            <div className="relative h-40 overflow-hidden bg-gradient-to-br from-primary-50 to-purple-50">
              <div className="absolute top-4 left-4">
                <div className="w-12 h-12 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                    <Play className="w-6 h-6 text-primary-600" />
                </div>
              </div>
                <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  lesson.isPremium 
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900' 
                    : 'bg-gradient-to-r from-green-400 to-green-500 text-green-900'
                }`}>
                  {lesson.isPremium ? '✨ Premium' : '🎉 Free'}
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {lesson.title}
                </h3>
                  <p className="text-sm text-gray-600">
                    {lesson.description}
                </p>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                    📚 {lesson.category}
                </span>
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium text-gray-600">{lesson.rating}</span>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                    <span>{lesson.duration}</span>
                </div>
                </div>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigation(lesson.href || `/quick-course/${lesson.slug}`, lesson.title, 'lesson', lesson._id);
                  }}
                  className={`w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-4 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 flex items-center justify-center group shadow-lg hover:shadow-xl ${navigatingItem === lesson._id ? 'opacity-75 cursor-not-allowed' : ''}`}
                  disabled={navigatingItem === lesson._id}
                >
                <Play className="w-5 h-5 mr-2" />
                  <span className="font-semibold">
                    {navigatingItem === lesson._id ? 'Starting...' : 'Start Learning'}
                  </span>
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        ))}

          {activeTab === 'quick-courses' && quickCourses.map((course) => (
            <div
              key={course._id}
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500"
            >
              <div className="relative p-6 text-white">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex flex-col space-y-2">
                    {course.isPremium && (
                      <div className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                        <Crown className="w-3 h-3 mr-1" />
                        Premium
                      </div>
                    )}
                    <div className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                      ⚡ Quick Win
                    </div>
                  </div>
                  </div>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-3">
                    {course.title}
                  </h3>
                  <p className="text-white/90 text-sm leading-relaxed">
                    {course.description}
                  </p>
                </div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4 text-white/90 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration}</span>
                    </div>
                    </div>
                  </div>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigation(`/quick-course/${course.slug}`, course.title, 'course', course._id);
                  }}
                  className={`w-full bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center group border border-white/30 hover:border-white/50 ${navigatingItem === course._id ? 'opacity-75 cursor-not-allowed' : ''}`}
                  disabled={navigatingItem === course._id}
                >
                  <span>{navigatingItem === course._id ? 'Starting...' : 'Start Learning'}</span>
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Empty State */}
      {((activeTab === 'journeys' && filteredPaths.length === 0) || 
        (activeTab === 'lessons' && lessons.length === 0) || 
        (activeTab === 'quick-courses' && quickCourses.length === 0)) && !loading && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No content found
            </h3>
            <p className="text-gray-600 mb-6">
              Don't worry! Try adjusting your search or explore different topics. We're here to help you find what you need.
            </p>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Clearing all filters');
              showToast('info', 'Filters cleared');
              setSearchTerm('');
              setSelectedFilter('all');
              setSelectedCategory('all');
            }}
              className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
Clear Filters
          </button>
        </div>
      )}
      </div>

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">
          Ready to Build Your American Dream?
        </h2>
        <p className="text-lg mb-6 opacity-90">
          You're not alone in this journey. Join thousands of immigrants who are successfully building their new lives in America with our step-by-step guidance.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Navigating to profile setup');
              showToast('info', 'Redirecting to profile setup');
              navigate('/profile-setup');
            }}
            className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
Get Started Free
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Navigating to subscription page');
              showToast('info', 'Redirecting to premium plans');
              navigate('/subscription');
            }}
            className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
View Premium Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuidesPage;
